import { i as isObject } from "./type_guards-BAhiOypL.mjs";
class FunctionSerializer {
  #namedBindings;
  #argNames;
  #originalParamString = "";
  #functionBody;
  #functionName = "";
  #isExpressionBody = false;
  #isAsync = false;
  #isGenerator = false;
  /**
   * Private constructor - use static methods instead
   */
  constructor(functionString, namedBindings) {
    this.#namedBindings = /* @__PURE__ */ new Map();
    this.#argNames = [];
    this.#functionBody = "";
    if (namedBindings && isObject(namedBindings)) {
      Object.entries(namedBindings).forEach(([key, value]) => {
        this.#namedBindings.set(key, value);
      });
    }
    const trimmed = functionString.trim();
    this.#parseArgNames(trimmed);
    this.#parseFunctionBody(trimmed);
  }
  /**
   * Check if a value can be serialized as a function
   * @param value - The value to check
   * @returns True if the value can be serialized, false otherwise
   */
  static canSerialize(value) {
    if (typeof value !== "function") {
      return false;
    }
    const fnString = value.toString();
    if (fnString.includes("[native code]")) {
      return false;
    }
    if (fnString.startsWith("function () { [native code] }")) {
      return false;
    }
    return true;
  }
  /**
   * Dehydrate a function into a serializable format
   * @param fn - The function to dehydrate
   * @param namedBindings - Optional named bindings for the function
   * @returns The dehydrated function value
   */
  static dehydrate(fn, namedBindings) {
    if (!FunctionSerializer.canSerialize(fn)) {
      throw new TypeError("Function cannot be serialized");
    }
    const functionString = fn.toString();
    const dehydrated = {
      _encodedType: "function",
      _encodedValueType: "string",
      _encodedValue: functionString
    };
    if (namedBindings) {
      dehydrated._namedBindings = namedBindings;
    }
    return dehydrated;
  }
  /**
   * Rehydrate a function from its dehydrated format or raw function string
   * @param input - The dehydrated function value or raw function string
   * @param namedBindings - Optional named bindings for the function
   * @returns The rehydrated function
   */
  static rehydrate(input, namedBindings) {
    let functionString;
    let bindings = namedBindings;
    if (typeof input === "string") {
      functionString = input;
    } else if (isObject(input) && input._encodedType === "function" && input._encodedValueType === "string" && typeof input._encodedValue === "string") {
      functionString = input._encodedValue;
      bindings = input._namedBindings || bindings;
    } else {
      throw new TypeError("Invalid input for function rehydration");
    }
    const serializer = new FunctionSerializer(functionString, bindings);
    return serializer.#buildFunction();
  }
  #parseArgNames(trimmed) {
    const patterns = [
      // Arrow function with parentheses: (a, b, c) => ...
      /^\s*(?:async\s+)?\(([^)]*)\)\s*=>/,
      // Arrow function single param without parentheses: x => ...
      /^\s*(?:async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=>/,
      // Regular/async function: function name(a, b, c) or function(a, b, c)
      /^\s*(?:async\s+)?function\s*(?:\*\s*)?(?:[a-zA-Z_$][a-zA-Z0-9_$]*)?\s*\(([^)]*)\)/,
      // Method syntax: methodName(a, b, c) { ... }
      /^\s*(?:async\s+)?(?:\*\s*)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(([^)]*)\)\s*\{/
    ];
    let paramString = "";
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match) {
        paramString = match[1] || match[2] || "";
        break;
      }
    }
    this.#originalParamString = paramString;
    if (!paramString || !paramString.trim()) {
      return;
    }
    const args = [];
    let current = "";
    let depth = 0;
    let inString = false;
    let stringChar = "";
    for (let i = 0; i < paramString.length; i++) {
      const char = paramString[i];
      const prevChar = i > 0 ? paramString[i - 1] : "";
      if ((char === '"' || char === "'" || char === "`") && prevChar !== "\\") {
        if (!inString) {
          inString = true;
          stringChar = char;
        } else if (char === stringChar) {
          inString = false;
        }
      }
      if (!inString) {
        if (char === "{" || char === "[" || char === "(") {
          depth++;
        } else if (char === "}" || char === "]" || char === ")") {
          depth--;
        }
        if (char === "," && depth === 0) {
          const processed = this.#processParameter(current.trim());
          if (processed) args.push(processed);
          current = "";
          continue;
        }
      }
      current += char;
    }
    if (current.trim()) {
      const processed = this.#processParameter(current.trim());
      if (processed) args.push(processed);
    }
    this.#argNames.push(...args);
  }
  #processParameter(param) {
    if (!param) return "";
    param = param.trim();
    if (param.startsWith("...")) {
      return param.slice(3).trim().split(/[\s=]/)[0];
    }
    if (param.startsWith("{") || param.startsWith("[")) {
      return param.split(/[\s=:]/)[0];
    }
    const nameMatch = param.match(/^([a-zA-Z_$][a-zA-Z0-9_$]*)/);
    return nameMatch ? nameMatch[1] : "";
  }
  #parseFunctionBody(trimmed) {
    this.#isAsync = /^\s*async\s+/.test(trimmed);
    const arrowExpressionMatch = trimmed.match(
      /^\s*(async\s+)?(?:\([^)]*\)|[a-zA-Z_$][a-zA-Z0-9_$]*)\s*=>\s*(.+)$/s
    );
    if (arrowExpressionMatch && !arrowExpressionMatch[2].trimStart().startsWith("{")) {
      this.#isExpressionBody = true;
      this.#functionBody = arrowExpressionMatch[2].trim();
      return;
    }
    const arrowBlockMatch = trimmed.match(
      /^\s*(async\s+)?(?:\([^)]*\)|[a-zA-Z_$][a-zA-Z0-9_$]*)\s*=>\s*\{([\s\S]*)\}\s*$/
    );
    if (arrowBlockMatch) {
      this.#functionBody = arrowBlockMatch[2].trim();
      return;
    }
    const functionMatch = trimmed.match(
      /^\s*(async\s+)?function\s*(\*\s*)?([a-zA-Z_$][a-zA-Z0-9_$]*)?\s*\([^)]*\)\s*\{([\s\S]*)\}\s*$/
    );
    if (functionMatch) {
      this.#isGenerator = !!functionMatch[2];
      this.#functionName = functionMatch[3] || "";
      this.#functionBody = functionMatch[4].trim();
      return;
    }
    const methodMatch = trimmed.match(
      /^\s*(async\s+)?(\*\s*)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\([^)]*\)\s*\{([\s\S]*)\}\s*$/
    );
    if (methodMatch) {
      this.#isGenerator = !!methodMatch[2];
      this.#functionName = methodMatch[3];
      this.#functionBody = methodMatch[4].trim();
      return;
    }
    const firstBrace = trimmed.indexOf("{");
    const lastBrace = trimmed.lastIndexOf("}");
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      this.#functionBody = trimmed.substring(firstBrace + 1, lastBrace);
      return;
    }
  }
  #buildFunction() {
    const functionBody = this.#isExpressionBody ? `return ${this.#functionBody}` : this.#functionBody;
    const bindingNames = Array.from(this.#namedBindings.keys());
    const namedBindingValues = Array.from(this.#namedBindings.values());
    const paramList = this.#originalParamString;
    let functionCode = "";
    const fnName = this.#functionName ? ` ${this.#functionName}` : "";
    if (this.#isAsync) {
      functionCode = this.#isGenerator ? `async function*${fnName}(${paramList}) { ${functionBody} }` : `async function${fnName}(${paramList}) { ${functionBody} }`;
    } else {
      functionCode = this.#isGenerator ? `function*${fnName}(${paramList}) { ${functionBody} }` : `function${fnName}(${paramList}) { ${functionBody} }`;
    }
    if (namedBindingValues.length > 0) {
      const boundParamNames = new Set(bindingNames.filter((name) => this.#argNames.includes(name)));
      const closureBindingNames = bindingNames.filter((name) => !boundParamNames.has(name));
      const closureParams = closureBindingNames.join(", ");
      const closureValues = closureBindingNames.map((name) => this.#namedBindings.get(name));
      const factoryCode = closureBindingNames.length > 0 ? `return function(${closureParams}) { return ${functionCode}; }` : `return ${functionCode}`;
      const factory = new Function(factoryCode)();
      const callable2 = closureBindingNames.length > 0 ? factory(...closureValues) : factory;
      if (boundParamNames.size > 0) {
        let wrapper = function(...args) {
          const actualArgs = [];
          let userArgIndex = 0;
          for (const param of parameterMapping) {
            if (param.isBound && param.boundIndex !== void 0) {
              actualArgs.push(namedBindingValues[param.boundIndex]);
            } else {
              actualArgs.push(args[userArgIndex++]);
            }
          }
          return callable2.call(this, ...actualArgs);
        };
        const parameterMapping = [];
        for (const argName of this.#argNames) {
          if (boundParamNames.has(argName)) {
            const boundIndex = bindingNames.indexOf(argName);
            parameterMapping.push({ name: argName, isBound: true, boundIndex });
          } else {
            parameterMapping.push({ name: argName, isBound: false });
          }
        }
        return wrapper;
      }
      return callable2;
    }
    const callable = new Function(`return ${functionCode}`)();
    return callable;
  }
}
export {
  FunctionSerializer
};
//# sourceMappingURL=function_serializer.mjs.map
